import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import { figmaTokens } from "assets/theme/designTokens";
import { getAssistantTourSteps, markAssistantTourDone } from "assistant/onboarding";

export default function AssistantWalkthrough({ open, language, onClose, onFinish }) {
  const steps = useMemo(() => getAssistantTourSteps(language), [language]);
  const [index, setIndex] = useState(0);
  const step = steps[index] || steps[0];
  const en = language === "en";
  const isLast = index >= steps.length - 1;

  const finish = () => {
    markAssistantTourDone();
    onFinish?.();
    onClose?.();
  };

  const handleNext = () => {
    if (isLast) {
      finish();
      return;
    }
    setIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const handleSkip = () => {
    finish();
  };

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "18px",
          overflow: "hidden",
          border: `1px solid ${figmaTokens.borderSubtle}`,
        },
      }}
    >
      <SoftBox
        px={2}
        py={1.5}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ background: figmaTokens.gradientDiagonalCSS, color: "#fff" }}
      >
        <SoftTypography variant="button" fontWeight="bold" color="white">
          {en ? "Quick tour" : "Visite guidée"}
        </SoftTypography>
        <IconButton size="small" onClick={handleSkip} sx={{ color: "#fff" }}>
          <Icon fontSize="small">close</Icon>
        </IconButton>
      </SoftBox>

      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        <SoftBox display="flex" gap={0.5} mb={2}>
          {steps.map((s, i) => (
            <SoftBox
              key={s.id}
              flex={1}
              height={4}
              borderRadius="999px"
              sx={{
                bgcolor: i <= index ? figmaTokens.accent : "rgba(15, 23, 42, 0.08)",
                transition: "background-color 0.25s ease",
              }}
            />
          ))}
        </SoftBox>

        <SoftBox
          width={52}
          height={52}
          borderRadius="14px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mb={1.5}
          sx={{ bgcolor: "rgba(102, 126, 234, 0.12)" }}
        >
          <Icon sx={{ color: figmaTokens.accent, fontSize: "1.6rem !important" }}>{step.icon}</Icon>
        </SoftBox>

        <SoftTypography variant="h6" fontWeight="bold" mb={1} sx={{ color: figmaTokens.textPrimary }}>
          {step.title}
        </SoftTypography>
        <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, lineHeight: 1.65, minHeight: 72 }}>
          {step.body}
        </SoftTypography>

        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mt={2.5}>
          <SoftButton variant="text" color="secondary" onClick={handleSkip} sx={{ fontWeight: 600 }}>
            {en ? "Skip" : "Passer"}
          </SoftButton>
          <SoftBox display="flex" gap={1}>
            {index > 0 && (
              <SoftButton variant="outlined" color="secondary" onClick={() => setIndex((i) => i - 1)}>
                {en ? "Back" : "Retour"}
              </SoftButton>
            )}
            <SoftButton variant="gradient" color="info" onClick={handleNext}>
              {isLast ? (en ? "Get started" : "Commencer") : en ? "Next" : "Suivant"}
            </SoftButton>
          </SoftBox>
        </SoftBox>

        <SoftTypography variant="caption" display="block" textAlign="center" mt={1.5} color="secondary">
          {index + 1} / {steps.length}
        </SoftTypography>
      </DialogContent>
    </Dialog>
  );
}

AssistantWalkthrough.propTypes = {
  open: PropTypes.bool.isRequired,
  language: PropTypes.string,
  onClose: PropTypes.func,
  onFinish: PropTypes.func,
};
